import type { ElementMetaEntry, StoreyLevel } from "../state";

/**
 * IndexedDB-кэш `elementMeta` по сигнатуре IFC-файла.
 *
 * Первое открытие IFC проходит через `buildElementMetaCache` со всеми
 * `await ifcManager.getItemProperties(...)`, что может занимать секунды
 * на больших моделях. Здесь сохраняем результат на диск, чтобы повторное
 * открытие того же файла было моментальным.
 */

const DB_NAME = "ifc-viewer-cache-v1";
const DB_VERSION = 1;
const META_STORE = "elementMeta";
const PAYLOAD_VERSION = 4; // v4: отметки этажей для плана сверху
const MAX_CACHED_FILES = 24;

export interface CachedMetaPayload {
  version: number;
  signature: string;
  createdAt: number;
  entries: [number, ElementMetaEntry][];
  storeyNames: string[];
  storeyLevels: StoreyLevel[];
  typeNames: string[];
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function isIndexedDbAvailable(): boolean {
  try {
    return typeof indexedDB !== "undefined" && indexedDB !== null;
  } catch (_) {
    return false;
  }
}

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) {
    return dbPromise;
  }
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    if (!isIndexedDbAvailable()) {
      resolve(null);
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (_) {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(META_STORE)) {
        const store = db.createObjectStore(META_STORE, { keyPath: "signature" });
        store.createIndex("createdAt", "createdAt", { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
  return dbPromise;
}

export async function loadMetaFromCache(signature: string): Promise<CachedMetaPayload | null> {
  const db = await openDb();
  if (!db) {
    return null;
  }
  return new Promise<CachedMetaPayload | null>((resolve) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(META_STORE, "readonly");
    } catch (_) {
      resolve(null);
      return;
    }
    const store = tx.objectStore(META_STORE);
    const req = store.get(signature);
    req.onsuccess = () => {
      const val = req.result as CachedMetaPayload | undefined;
      if (!val || val.version !== PAYLOAD_VERSION || val.signature !== signature) {
        resolve(null);
        return;
      }
      resolve(val);
    };
    req.onerror = () => resolve(null);
  });
}

export async function saveMetaToCache(
  signature: string,
  data: {
    entries: [number, ElementMetaEntry][];
    storeyNames: string[];
    storeyLevels: StoreyLevel[];
    typeNames: string[];
  },
): Promise<void> {
  const db = await openDb();
  if (!db) {
    return;
  }
  const payload: CachedMetaPayload = {
    version: PAYLOAD_VERSION,
    signature,
    createdAt: Date.now(),
    entries: data.entries,
    storeyNames: data.storeyNames,
    storeyLevels: data.storeyLevels,
    typeNames: data.typeNames,
  };
  await new Promise<void>((resolve) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(META_STORE, "readwrite");
    } catch (_) {
      resolve();
      return;
    }
    const store = tx.objectStore(META_STORE);
    const req = store.put(payload);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
  });
  // Лучшая попытка: ограничиваем размер кэша, без блокировки текущего вызова.
  void enforceCacheLimit(db);
}

async function enforceCacheLimit(db: IDBDatabase): Promise<void> {
  try {
    const all = await new Promise<CachedMetaPayload[]>((resolve) => {
      const tx = db.transaction(META_STORE, "readonly");
      const store = tx.objectStore(META_STORE);
      const req = store.getAll();
      req.onsuccess = () => resolve((req.result as CachedMetaPayload[]) || []);
      req.onerror = () => resolve([]);
    });
    if (all.length <= MAX_CACHED_FILES) {
      return;
    }
    all.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    const toDrop = all.slice(0, all.length - MAX_CACHED_FILES);
    await new Promise<void>((resolve) => {
      const tx = db.transaction(META_STORE, "readwrite");
      const store = tx.objectStore(META_STORE);
      for (const item of toDrop) {
        store.delete(item.signature);
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch (_) {
    /* ignore */
  }
}

export function getIfcFileCacheSignature(file: File): string {
  return `${file.name}::${file.size}::${file.lastModified}`;
}
